import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Loader2, CheckCircle2, MessageCircle, ArrowRight } from "lucide-react";
import { buildWhatsAppUrl } from "@/components/WhatsAppButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = {
  name: "",
  email: "",
  phone: "",
  event_date: "",
  event_type: "",
  location: "",
  guest_count: "",
  message: "",
};

const Contact = () => {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // holds submitted form payload

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const buildWhatsAppMessage = (data) => {
    const lines = [
      "Hi Bon Evento! I just submitted an enquiry on your website.",
      "",
      `Name: ${data.name}`,
      data.phone && `Phone: ${data.phone}`,
      `Email: ${data.email}`,
      data.event_type && `Event: ${data.event_type}`,
      data.event_date && `Date: ${data.event_date}`,
      data.location && `Location: ${data.location}`,
      data.guest_count && `Guests: ${data.guest_count}`,
      "",
      `Message: ${data.message}`,
    ].filter(Boolean);
    return lines.join("\n");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please share your name, email and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setSubmitted(form);
      toast.success(
        "Thank you. Your enquiry has been received. Our team will contact you shortly."
      );
      setForm(initialState);
      setTimeout(() => {
        document
          .getElementById("contact")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const failureMsg =
        (detail && typeof detail === "object" && detail.message) ||
        "We couldn't send your enquiry. Please try again or contact us on WhatsApp.";
      toast.error(failureMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-40 px-6 md:px-12 lg:px-24 bg-[#0A0D17]"
    >
      <div className="grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5 reveal">
          <div className="overline mb-6">Get In Touch</div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-7xl font-light leading-[1] tracking-tight">
            Let's plan
            <br />
            something
            <br />
            <span className="italic text-[#B8935C]">unforgettable</span>.
          </h2>

          <p className="mt-10 text-[#F0EBE1]/65 text-base sm:text-lg leading-relaxed font-light max-w-md">
            Share a little about your celebration. We respond personally to
            every enquiry within two working days.
          </p>

          <div className="mt-12 space-y-6 border-t border-[#F0EBE1]/10 pt-10">
            <div className="flex items-start gap-4" data-testid="contact-phone">
              <Phone size={18} className="text-[#B8935C] mt-1 flex-shrink-0" strokeWidth={1.4} />
              <div>
                <div className="overline mb-1 text-[10px]">Call / WhatsApp</div>
                <div className="text-[#F0EBE1]/85 font-light leading-relaxed">
                  <a href="tel:+919920261283" className="block hover:text-[#B8935C] transition-colors">
                    +91 99202 61283
                  </a>
                  <a href="tel:+918928469578" className="block hover:text-[#B8935C] transition-colors">
                    +91 89284 69578
                  </a>
                </div>
              </div>
            </div>
            <div className="flex items-start gap-4" data-testid="contact-email">
              <Mail size={18} className="text-[#B8935C] mt-1 flex-shrink-0" strokeWidth={1.4} />
              <div>
                <div className="overline mb-1 text-[10px]">Write</div>
                <a
                  href="mailto:connect@bonevento.com"
                  className="text-[#F0EBE1]/85 font-light hover:text-[#B8935C] transition-colors"
                >
                  connect@bonevento.com
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4" data-testid="contact-address">
              <MapPin size={18} className="text-[#B8935C] mt-1 flex-shrink-0" strokeWidth={1.4} />
              <div>
                <div className="overline mb-1 text-[10px]">Visit</div>
                <div className="text-[#F0EBE1]/85 font-light leading-relaxed">
                  Aston Building, 10th Floor,
                  <br />
                  Andheri West, Mumbai 400053
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 lg:pl-10 reveal">
          {submitted ? (
            <div
              data-testid="contact-success"
              className="relative bg-[#121623] border border-[#B8935C]/40 p-8 md:p-12 overflow-hidden"
            >
              <div
                aria-hidden
                className="absolute -top-20 -right-20 w-[320px] h-[320px] rounded-full bg-[#B8935C]/15 blur-[100px]"
              />
              <div className="relative">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#B8935C]/15 border border-[#B8935C]/40 mb-7">
                  <CheckCircle2 size={28} strokeWidth={1.3} className="text-[#B8935C]" />
                </div>
                <div className="overline mb-4">Enquiry Received</div>
                <h3 className="font-serif text-3xl md:text-4xl font-light leading-tight text-[#F0EBE1]">
                  Thank you,&nbsp;
                  <span className="italic text-[#B8935C]">
                    {submitted.name.split(" ")[0] || "friend"}
                  </span>
                  .
                </h3>
                <p className="mt-5 text-[#F0EBE1]/70 text-base leading-relaxed font-light max-w-lg">
                  Your enquiry has reached the Bon Evento studio. Nishi will
                  personally write back within two working days. For an
                  instant conversation, continue with us on WhatsApp —
                  we'll have your details ready.
                </p>

                <div className="mt-9 flex flex-wrap items-center gap-4">
                  <a
                    href={buildWhatsAppUrl(buildWhatsAppMessage(submitted))}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="success-whatsapp-btn"
                    className="inline-flex items-center gap-3 px-8 py-4 bg-[#B8935C] text-[#080B12] text-xs uppercase tracking-[0.28em] font-medium hover:bg-[#D4AF37] transition-all duration-500"
                  >
                    <MessageCircle size={14} />
                    Continue on WhatsApp
                  </a>
                  <button
                    type="button"
                    onClick={() => setSubmitted(null)}
                    data-testid="success-new-enquiry-btn"
                    className="inline-flex items-center gap-2 px-6 py-4 border border-[#F0EBE1]/20 text-[#F0EBE1]/80 text-xs uppercase tracking-[0.28em] hover:border-[#B8935C] hover:text-[#B8935C] transition-all duration-500"
                  >
                    Send Another <ArrowRight size={14} />
                  </button>
                </div>

                <div className="mt-10 pt-6 border-t border-[#F0EBE1]/10 text-[#F0EBE1]/45 text-xs font-light leading-relaxed">
                  A copy has also been delivered to <span className="text-[#B8935C]">connect@bonevento.com</span>.
                </div>
              </div>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className=""
            >
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
                <div>
                  <label className="overline block mb-1" htmlFor="name">
                    Your Name *
                  </label>
                  <input
                    id="name"
                    name="name"
                    data-testid="input-name"
                    value={form.name}
                    onChange={onChange}
                    placeholder="Anaya Sharma"
                    className="input-editorial"
                    required
                    autoComplete="name"
                  />
                </div>
                <div>
                  <label className="overline block mb-1" htmlFor="email">
                    Email *
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    data-testid="input-email"
                    value={form.email}
                    onChange={onChange}
                    placeholder="you@example.com"
                    className="input-editorial"
                    required
                    autoComplete="email"
                  />
                </div>
                <div>
                  <label className="overline block mb-1 mt-6" htmlFor="phone">
                    Phone Number *
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    inputMode="tel"
                    data-testid="input-phone"
                    value={form.phone}
                    onChange={onChange}
                    placeholder="+91 98XXX XXXXX"
                    className="input-editorial"
                    autoComplete="tel"
                  />
                </div>
                <div>
                  <label className="overline block mb-1 mt-6" htmlFor="event_date">
                    Event Date
                  </label>
                  <input
                    id="event_date"
                    name="event_date"
                    data-testid="input-date"
                    value={form.event_date}
                    onChange={onChange}
                    placeholder="December 2026"
                    className="input-editorial"
                  />
                </div>
                <div>
                  <label className="overline block mb-1 mt-6" htmlFor="event_type">
                    Event Type
                  </label>
                  <input
                    id="event_type"
                    name="event_type"
                    data-testid="input-event-type"
                    value={form.event_type}
                    onChange={onChange}
                    placeholder="Wedding · Sangeet · Reception"
                    className="input-editorial"
                  />
                </div>
                <div>
                  <label className="overline block mb-1 mt-6" htmlFor="location">
                    Preferred Location
                  </label>
                  <input
                    id="location"
                    name="location"
                    data-testid="input-location"
                    value={form.location}
                    onChange={onChange}
                    placeholder="Mumbai · Goa · Italy"
                    className="input-editorial"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="overline block mb-1 mt-6" htmlFor="guest_count">
                    Guest Count
                  </label>
                  <input
                    id="guest_count"
                    name="guest_count"
                    data-testid="input-guests"
                    value={form.guest_count}
                    onChange={onChange}
                    placeholder="250 guests"
                    className="input-editorial"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="overline block mb-1 mt-6" htmlFor="message">
                    Message / Requirements *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    data-testid="input-message"
                    value={form.message}
                    onChange={onChange}
                    rows={4}
                    placeholder="Share a little about your love story, your vision, what you're dreaming of..."
                    className="input-editorial resize-none"
                    required
                  />
                </div>
              </div>

              <div className="mt-12 flex flex-wrap items-center gap-4">
                <button
                  type="submit"
                  data-testid="submit-inquiry-btn"
                  disabled={submitting}
                  className="inline-flex items-center gap-3 px-10 py-4 bg-[#B8935C] text-[#080B12] text-xs uppercase tracking-[0.28em] font-medium hover:bg-[#D4AF37] transition-all duration-500 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={14} className="animate-spin" /> Sending
                    </>
                  ) : (
                    <>Send Enquiry</>
                  )}
                </button>

                <a
                  href={buildWhatsAppUrl(
                    form.name || form.message
                      ? buildWhatsAppMessage(form)
                      : undefined
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="form-whatsapp-btn"
                  className="inline-flex items-center gap-3 px-7 py-4 border border-[#F0EBE1]/20 text-[#F0EBE1]/80 text-xs uppercase tracking-[0.28em] hover:border-[#B8935C] hover:text-[#B8935C] transition-all duration-500"
                >
                  <MessageCircle size={14} />
                  Or WhatsApp Us
                </a>
              </div>

              <div className="mt-6 text-[#F0EBE1]/40 text-xs font-light leading-relaxed">
                We respond personally within two working days. All enquiries
                are delivered to&nbsp;
                <span className="text-[#B8935C]">connect@bonevento.com</span>.
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default Contact;
